import { ToastQueue } from "@adobe/react-spectrum"

const DEFAULT_TOAST_SUPPRESSION_MS = 5_000

let nextToastAt = 0

function formatRetryAfterSeconds(retryAfterSeconds: number): string {
  return retryAfterSeconds === 1
    ? "Too many requests right now. Try again in 1 second."
    : `Too many requests right now. Try again in ${retryAfterSeconds} seconds.`
}

function getRateLimitMessage(retryAfterSeconds: number | null): string {
  return retryAfterSeconds === null
    ? "Too many requests right now. Please wait and try again."
    : formatRetryAfterSeconds(retryAfterSeconds)
}

function getToastTimeoutMs(retryAfterSeconds: number | null): number {
  return Math.max(
    retryAfterSeconds === null
      ? DEFAULT_TOAST_SUPPRESSION_MS
      : retryAfterSeconds * 1000,
    DEFAULT_TOAST_SUPPRESSION_MS,
  )
}

export function parseRetryAfterSeconds(
  retryAfterHeader: string | null,
  now = Date.now(),
): number | null {
  if (!retryAfterHeader) {
    return null
  }

  const trimmed = retryAfterHeader.trim()
  if (!trimmed) {
    return null
  }

  const retryAfterSeconds = Number(trimmed)
  if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds >= 0) {
    return Math.ceil(retryAfterSeconds)
  }

  const retryAfterAt = Date.parse(trimmed)
  if (Number.isNaN(retryAfterAt)) {
    return null
  }

  const secondsUntilRetry = Math.ceil((retryAfterAt - now) / 1000)
  return secondsUntilRetry > 0 ? secondsUntilRetry : null
}

function notifyRateLimit(retryAfterSeconds: number | null): void {
  const now = Date.now()
  if (now < nextToastAt) {
    return
  }

  const timeout = getToastTimeoutMs(retryAfterSeconds)
  ToastQueue.negative(getRateLimitMessage(retryAfterSeconds), { timeout })
  nextToastAt = now + timeout
}

export class RateLimitError extends Error {
  readonly retryAfterSeconds: number | null

  constructor(retryAfterSeconds: number | null) {
    super(getRateLimitMessage(retryAfterSeconds))
    this.name = "RateLimitError"
    this.retryAfterSeconds = retryAfterSeconds
  }
}

export function isRateLimitError(error: unknown): error is RateLimitError {
  return error instanceof RateLimitError
}

export function throwIfRateLimited(response: Response): void {
  if (response.status !== 429 && response.status !== 409) {
    return
  }

  const retryAfterSeconds = parseRetryAfterSeconds(
    response.headers.get("Retry-After"),
  )
  notifyRateLimit(retryAfterSeconds)
  throw new RateLimitError(retryAfterSeconds)
}

export function resetRateLimitToastStateForTests(): void {
  nextToastAt = 0
}
